import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import NavPills from 'components/NavPills/NavPills.js';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import { useGuilds } from '../../hooks/useGuilds';
import { CircularProgress, Alert } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import config from '../../config/config.json';

import d2rsIcon from '../../assets/img/d2rsiconblock.png';
import {
  FestivalOutlined,
  RuleOutlined,
  RepeatOutlined,
  EventOutlined,
  PublicOutlined,
  EmojiEventsOutlined,
  GroupOutlined,
  PersonAddOutlined,
  ExitToAppOutlined,
} from '@mui/icons-material';
import { Chip, Button, IconButton, Tooltip, Box, Avatar, Typography } from '@mui/material';
import { useUserData } from '../../services/UserService';
import { secondaryColor } from '../../assets/jss/material-kit-react';
const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableHeader: {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    borderBottom: `2px solid ${theme.palette.divider}`,
    '& th': {
      padding: '12px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
  },
  tableRow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& td': {
      padding: '12px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
  },
  rulesList: {
    display: 'block',
    padding: '20px',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '5px',
    color: theme.palette.text.primary,
  },
}));

// Component for participant actions
const ParticipantActions = ({ tour, tourParticipants }: { tour: any; tourParticipants: any[] }) => {
  const { t } = useTranslation();
  const sessionToken = localStorage.getItem('session_token');
  const { data: userData } = useUserData();
  const { fetchGuilds } = useGuilds();

  // Handle both old and new tour structure
  const maxParticipants = tour.max_participants || tour.tour?.max_participants || 0;

  // Get participant data from the tour participants passed in
  const participants = tourParticipants || [];
  const participantCount = participants.length;

  // Check if current user is a participant
  const currentUserId = userData?.user?.id;
  const isParticipant = currentUserId
    ? participants.some((p) => p.user_id === currentUserId)
    : false;

  // Create participants tooltip content
  const ParticipantsTooltip = () => {
    if (participants.length === 0) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('tours.participants.noParticipants')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1, maxWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('tours.participants.list')}
        </Typography>
        {participants.map((participant, index) => (
          <Box
            key={participant.id || index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: index < participants.length - 1 ? 1 : 0,
              gap: 1,
            }}
          >
            <Avatar
              src={participant.profile_image_path}
              alt={participant.username}
              sx={{ width: 24, height: 24 }}
            >
              {participant.username?.charAt(0).toUpperCase() || '?'}
            </Avatar>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {participant.username || 'Unknown'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Join tour mutation
  const joinTourMutation = useMutation({
    mutationFn: async (tourId: string) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['session-token'] = sessionToken;
      }

      const response = await fetch(`${config.backend_base_url}api/tour/${tourId}/join`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to join tour';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Response is not JSON, use status text or default message
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Handle empty responses or non-JSON responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Return a success indicator for non-JSON responses
        return { success: true };
      }
    },
    onSuccess: () => {
      toast.success(t('tours.messages.joinSuccess'));
      // Refetch guilds data to update participants
      fetchGuilds();
    },
    onError: (error) => {
      console.error('Failed to join tour:', error);
      toast.error(error.message || t('tours.messages.joinError'));
    },
  });

  // Leave tour mutation
  const leaveTourMutation = useMutation({
    mutationFn: async (tourId: string) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['session-token'] = sessionToken;
      }

      const response = await fetch(`${config.backend_base_url}api/tour/${tourId}/leave`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to leave tour';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Response is not JSON, use status text or default message
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Handle empty responses or non-JSON responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Return a success indicator for non-JSON responses
        return { success: true };
      }
    },
    onSuccess: () => {
      toast.success(t('tours.messages.leaveSuccess'));
      // Refetch guilds data to update participants
      fetchGuilds();
    },
    onError: (error) => {
      console.error('Failed to leave tour:', error);
      toast.error(error.message || t('tours.messages.leaveError'));
    },
  });

  if (!sessionToken) {
    return (
      <Tooltip
        title={<ParticipantsTooltip />}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
            },
          },
        }}
      >
        <span>
          <Chip
            icon={<GroupOutlined />}
            label={
              maxParticipants === 0
                ? `${participantCount}`
                : `${participantCount} / ${maxParticipants}`
            }
            size="small"
            variant="outlined"
          />
        </span>
      </Tooltip>
    );
  }

  // If max_participants is 0, treat as unlimited
  const isUnlimited = maxParticipants === 0;
  const isFull = !isUnlimited && participantCount >= maxParticipants;

  const handleJoinTour = () => {
    if (tour.id) {
      joinTourMutation.mutate(tour.id.toString());
    }
  };

  const handleLeaveTour = () => {
    if (tour.id) {
      leaveTourMutation.mutate(tour.id.toString());
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Tooltip
        title={<ParticipantsTooltip />}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
            },
          },
        }}
      >
        <Chip
          icon={<GroupOutlined />}
          label={
            isUnlimited
              ? `${participantCount}`
              : t('tours.participants.count', {
                  count: participantCount,
                  max: maxParticipants,
                })
          }
          size="small"
          variant="outlined"
          color={'default'}
        />
      </Tooltip>

      {isParticipant ? (
        <Tooltip title={t('tours.participants.leave')}>
          <IconButton
            size="small"
            onClick={handleLeaveTour}
            color="error"
            disabled={leaveTourMutation.isPending}
          >
            {leaveTourMutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <ExitToAppOutlined fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={isFull ? t('tours.participants.full') : t('tours.participants.join')}>
          <span>
            <IconButton
              size="small"
              onClick={handleJoinTour}
              color="secondary"
              disabled={isFull || joinTourMutation.isPending}
            >
              {joinTourMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <PersonAddOutlined fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default function TourPagesPage() {
  const { t } = useTranslation();
  const classes = useStyles();
  useDocumentTitle(t('navigation.tourPages'));

  // Use guilds slice
  const { guilds, loading, error, fetchGuilds } = useGuilds();

  // Initialize data on mount
  useEffect(() => {
    fetchGuilds();
  }, [fetchGuilds]);

  // Get all tours with their guild context
  const toursWithGuild = guilds.flatMap((guild) =>
    (guild.tours || []).map((tour) => ({
      ...tour,
      guild: guild,
      // Normalize tour data structure
      tourData: tour.tour || tour,
    }))
  );

  // Extract all rulesets from guilds
  const rulesets = guilds.flatMap((guild) => guild.rulesets || []);

  const handleChange = () => {};

  // Show loading state
  if (loading.guilds) {
    return (
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardBody>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <CircularProgress />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error.guilds) {
    return (
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardBody>
                <Alert severity="error">
                  Failed to load data. Please try again later.
                  <div style={{ marginTop: '10px' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        fetchGuilds();
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                </Alert>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Get the initial active tab from the URL
  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardHeader color="primary">
                <h4>Tour Schedule</h4>
              </CardHeader>
              <CardBody>
                <NavPills
                  queryKey="t"
                  alignCenter
                  fullWidth
                  onChange={handleChange}
                  color="primary"
                  tabs={[
                    {
                      tabName: 'tours',
                      tabButton: (
                        <>
                          {t('search.navPills.tourSchedule')}
                          <FestivalOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                        </>
                      ),
                      tabContent: (
                        <span>
                          <div className="container">
                            <NavPills
                              queryKey={'o'}
                              alignCenter
                              color="primary"
                              onChange={handleChange}
                              tabs={[
                                {
                                  tabName: 'all',
                                  tabButton: (
                                    <span style={{ display: 'block' }}>
                                      <img
                                        style={{ width: '64px' }}
                                        src={d2rsIcon}
                                        alt="All Tours"
                                      />
                                      All Tours
                                    </span>
                                  ),
                                  tabContent: (
                                    <span>
                                      <div style={{ overflowX: 'auto' }}>
                                        <table
                                          className="table"
                                          style={{
                                            width: '100%',
                                            minWidth: '900px',
                                            borderCollapse: 'collapse',
                                            margin: '20px 0',
                                            fontSize: '18px',
                                            textAlign: 'left',
                                          }}
                                        >
                                          <thead>
                                            <tr className={classes.tableHeader}>
                                              <th>Title</th>
                                              <th>Date</th>
                                              <th>Type</th>
                                              <th>Realm</th>
                                              <th>Ruleset</th>
                                              <th>Prize</th>
                                              <th>Participants</th>
                                              <th>Guild</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {toursWithGuild.map((tour, index) => {
                                              const ruleset = rulesets.find(
                                                (r) => r.id === tour.tourData.ruleset_id
                                              );

                                              return (
                                                <tr
                                                  key={tour.tourData.id || index}
                                                  className={classes.tableRow}
                                                >
                                                  <td>
                                                    <Link
                                                      to={`/tournament/${tour.tourData.id}`}
                                                      style={{
                                                        textDecoration: 'none',
                                                        color: 'inherit',
                                                      }}
                                                    >
                                                      {tour.tourData.name}
                                                    </Link>
                                                  </td>
                                                  <td>
                                                    {tour.tourData.date
                                                      ? new Date(
                                                          tour.tourData.date
                                                        ).toLocaleString()
                                                      : 'TBD'}
                                                  </td>
                                                  <td>
                                                    <Chip
                                                      icon={
                                                        tour.tourData.type ===
                                                        'recurring_weekly' ? (
                                                          <RepeatOutlined />
                                                        ) : (
                                                          <EventOutlined />
                                                        )
                                                      }
                                                      label={
                                                        tour.tourData.type === 'recurring_weekly'
                                                          ? 'Weekly'
                                                          : 'One-time'
                                                      }
                                                      size="small"
                                                      color={
                                                        tour.tourData.type === 'recurring_weekly'
                                                          ? 'primary'
                                                          : 'secondary'
                                                      }
                                                    />
                                                  </td>
                                                  <td>
                                                    <Chip
                                                      icon={<PublicOutlined />}
                                                      label={
                                                        tour.tourData.realm
                                                          ? tour.tourData.realm
                                                              .charAt(0)
                                                              .toUpperCase() +
                                                            tour.tourData.realm.slice(1)
                                                          : 'TBD'
                                                      }
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  </td>
                                                  <td>
                                                    {ruleset ? (
                                                      <Link
                                                        to={`/ruleset/${ruleset.id}`}
                                                        style={{
                                                          textDecoration: 'none',
                                                          color: 'inherit',
                                                        }}
                                                      >
                                                        {ruleset.name}
                                                      </Link>
                                                    ) : (
                                                      'Standard'
                                                    )}
                                                  </td>
                                                  <td>
                                                    <Chip
                                                      icon={<EmojiEventsOutlined />}
                                                      label={tour.tourData.prize || 'TBD'}
                                                      size="small"
                                                      color="warning"
                                                      variant="outlined"
                                                    />
                                                  </td>
                                                  <td>
                                                    <ParticipantActions
                                                      tour={tour.tourData}
                                                      tourParticipants={tour.participants || []}
                                                    />
                                                  </td>
                                                  <td>
                                                    {tour.guild ? (
                                                      <Link
                                                        to={`/guild/${tour.guild.team?.id}`}
                                                        style={{
                                                          textDecoration: 'none',
                                                          color: 'inherit',
                                                        }}
                                                      >
                                                        {tour.guild.team?.name}
                                                      </Link>
                                                    ) : (
                                                      'Unknown'
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                            {toursWithGuild.length === 0 && (
                                              <tr className={classes.tableRow}>
                                                <td
                                                  colSpan={8}
                                                  style={{ textAlign: 'center', padding: '20px' }}
                                                >
                                                  No tours scheduled at the moment.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </span>
                                  ),
                                },
                                ...guilds
                                  .filter((guild) => guild.tours && guild.tours.length > 0)
                                  .map((guild, index) => ({
                                    tabName: guild.team.name,
                                    tabButton: (
                                      <div className={classes.tabButton} key={index}>
                                        {guild.team.logo_url && (
                                          <img
                                            style={{ width: '32px', marginRight: '8px' }}
                                            src={guild.team.logo_url}
                                            alt={guild.team.name}
                                          />
                                        )}
                                        {guild.team.name}
                                      </div>
                                    ),
                                    tabContent: (
                                      <span key={index}>
                                        <div style={{ overflowX: 'auto' }}>
                                          <table
                                            className="table"
                                            style={{
                                              width: '100%',
                                              minWidth: '900px',
                                              borderCollapse: 'collapse',
                                              margin: '20px 0',
                                              fontSize: '18px',
                                              textAlign: 'left',
                                            }}
                                          >
                                            <thead>
                                              <tr className={classes.tableHeader}>
                                                <th>Title</th>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>Realm</th>
                                                <th>Ruleset</th>
                                                <th>Prize</th>
                                                <th>Participants</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(guild.tours || []).map((tour, tourIndex) => {
                                                // Normalize tour data structure
                                                const tourData = tour.tour || tour;
                                                const ruleset = rulesets.find(
                                                  (r) => r.id === tourData.ruleset_id
                                                );
                                                return (
                                                  <tr
                                                    key={tourData.id || `${index}-${tourIndex}`}
                                                    className={classes.tableRow}
                                                  >
                                                    <td>
                                                      <Link
                                                        to={`/tournament/${tourData.id}`}
                                                        style={{
                                                          textDecoration: 'none',
                                                          color: 'inherit',
                                                        }}
                                                      >
                                                        {tourData.name}
                                                      </Link>
                                                    </td>
                                                    <td>
                                                      {tourData.date
                                                        ? new Date(tourData.date).toLocaleString()
                                                        : 'TBD'}
                                                    </td>
                                                    <td>
                                                      <Chip
                                                        icon={
                                                          tourData.type === 'recurring_weekly' ? (
                                                            <RepeatOutlined />
                                                          ) : (
                                                            <EventOutlined />
                                                          )
                                                        }
                                                        label={
                                                          tourData.type === 'recurring_weekly'
                                                            ? 'Weekly'
                                                            : 'One-time'
                                                        }
                                                        size="small"
                                                        color={
                                                          tourData.type === 'recurring_weekly'
                                                            ? 'primary'
                                                            : 'secondary'
                                                        }
                                                      />
                                                    </td>
                                                    <td>
                                                      <Chip
                                                        icon={<PublicOutlined />}
                                                        label={
                                                          tourData.realm
                                                            ? tourData.realm
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                              tourData.realm.slice(1)
                                                            : 'TBD'
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                      />
                                                    </td>
                                                    <td>
                                                      {ruleset ? (
                                                        <Link
                                                          to={`/ruleset/${ruleset.id}`}
                                                          style={{
                                                            textDecoration: 'none',
                                                            color: 'inherit',
                                                          }}
                                                        >
                                                          {ruleset.name}
                                                        </Link>
                                                      ) : (
                                                        'Standard'
                                                      )}
                                                    </td>
                                                    <td>
                                                      <Chip
                                                        icon={<EmojiEventsOutlined />}
                                                        label={tourData.prize || 'TBD'}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                      />
                                                    </td>
                                                    <td>
                                                      <ParticipantActions
                                                        tour={tourData}
                                                        tourParticipants={tour.participants || []}
                                                      />
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </span>
                                    ),
                                  })),
                              ]}
                            />
                          </div>
                        </span>
                      ),
                    },
                    {
                      tabName: 'rulesets',
                      tabButton: (
                        <span style={{ display: 'block' }}>
                          {t('search.navPills.ruleSets')}{' '}
                          <RuleOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                        </span>
                      ),
                      tabContent: (
                        <span>
                          <div className="container">
                            <NavPills
                              queryKey={'rulesetGuild'}
                              alignCenter
                              color="primary"
                              onChange={handleChange}
                              tabs={guilds
                                .filter((guild) => {
                                  // Filter guilds that have rulesets
                                  return guild.rulesets && guild.rulesets.length > 0;
                                })
                                .map((guild, index) => {
                                  const guildRulesets = guild.rulesets || [];
                                  return {
                                    tabName: guild.team.name,
                                    tabButton: (
                                      <div className={classes.tabButton} key={index}>
                                        {guild.team.name}
                                        {guild.team.logo_url && (
                                          <img
                                            style={{ width: '24px', marginLeft: '8px' }}
                                            src={guild.team.logo_url}
                                            alt={guild.team.name}
                                          />
                                        )}
                                      </div>
                                    ),
                                    tabContent: (
                                      <span key={index}>
                                        {guildRulesets.length > 0 ? (
                                          <NavPills
                                            queryKey={`ruleset-${guild.team.id}`}
                                            alignCenter
                                            color="primary"
                                            onChange={handleChange}
                                            tabs={guildRulesets.map((ruleset, rulesetIndex) => ({
                                              tabName: ruleset.name,
                                              tabButton: (
                                                <span
                                                  style={{ display: 'block' }}
                                                  key={rulesetIndex}
                                                >
                                                  {ruleset.name}
                                                </span>
                                              ),
                                              tabContent: (
                                                <div
                                                  className={classes.rulesList}
                                                  key={rulesetIndex}
                                                >
                                                  <h2
                                                    style={{
                                                      color: secondaryColor,
                                                      marginTop: '0',
                                                    }}
                                                  >
                                                    <Link
                                                      to={`/ruleset/${ruleset.id}`}
                                                      style={{
                                                        textDecoration: 'none',
                                                        color: 'inherit',
                                                      }}
                                                    >
                                                      {ruleset.name}
                                                    </Link>
                                                  </h2>
                                                  {ruleset.description ? (
                                                    <p style={{ marginBottom: '20px' }}>
                                                      {ruleset.description}
                                                    </p>
                                                  ) : (
                                                    <p>
                                                      No description available for this ruleset.
                                                    </p>
                                                  )}
                                                  <div style={{ marginTop: '20px' }}>
                                                    <Button
                                                      variant="outlined"
                                                      component={Link}
                                                      to={`/ruleset/${ruleset.id}`}
                                                      size="small"
                                                    >
                                                      {t('search.viewDetails')}
                                                    </Button>
                                                  </div>
                                                </div>
                                              ),
                                            }))}
                                          />
                                        ) : (
                                          <div className={classes.rulesList}>
                                            <p style={{ textAlign: 'center', padding: '40px' }}>
                                              No rulesets available for this guild.
                                            </p>
                                          </div>
                                        )}
                                      </span>
                                    ),
                                  };
                                })}
                            />
                            {guilds.filter((guild) => guild.rulesets && guild.rulesets.length > 0)
                              .length === 0 && (
                              <div className={classes.rulesList}>
                                <p style={{ textAlign: 'center', padding: '40px' }}>
                                  No rulesets available at the moment.
                                </p>
                              </div>
                            )}
                          </div>
                        </span>
                      ),
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="tourPages" helpText={t('help.tourPages')} />
    </div>
  );
}
